import { useEffect, useMemo, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays, CalendarRange, CheckCircle2, ChevronRight, ExternalLink, Hourglass, Link2Off, List, Lock, Mail, MessageSquareText, PenLine,
  RefreshCw, WifiOff,
} from 'lucide-react';
import {
  alsTag, anredeName, darfUeberLinkSchreiben, linkAnmeldungFehlt, linkUebersicht, plusTage, tagIso, wochenStart, type LinkPerson, type PlanungsMail,
} from '../../lib/planung';
import { PlanungsListe } from '../../components/Planung/PlanungsListe';
import { PlanungsKalender } from '../../components/Planung/PlanungsKalender';
import { FeedbackMailAnsicht } from './FeedbackMailAnsicht';
import { AdminAnsichtBanner, AnmeldeBanner, AnmeldeSeite } from './Anmeldung';
import { useAnmeldeKennung } from './linkAnmeldung';
import './Feedback.css';

const TOKEN_RE = /^[0-9a-f]{64}$/;
const UNGUELTIG = 'Dieser Link ist nicht (mehr) gültig – bitte frag bei der Person nach, die ihn Dir geschickt hat.';

/** Persönliche Links gehören in keine Suchmaschine und in keinen Referrer. */
function useLinkSchutz() {
  useEffect(() => {
    const setzen = (name: string, inhalt: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      const neu = !el;
      const vorher = el?.getAttribute('content') ?? null;
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = inhalt;
      const meta = el;
      return () => {
        if (neu) meta.remove();
        else if (vorher !== null) meta.setAttribute('content', vorher);
      };
    };
    const zurueck = [setzen('robots', 'noindex, nofollow'), setzen('referrer', 'no-referrer')];
    const titel = document.title;
    return () => {
      zurueck.forEach((f) => f());
      document.title = titel;
    };
  }, []);
}

function sichtSatz(p: LinkPerson): string {
  if (p.sicht === 'alle') return 'alle Mails, die wir gerade vorbereiten oder kürzlich verschickt haben';
  if (p.sicht === 'sportschulen' && p.sportschulen.length) return `die Mails für ${p.sportschulen.join(', ')} — und alle, zu denen wir Dich gefragt haben`;
  return 'die Mails, zu denen wir Dich um Rückmeldung bitten';
}

function Kopf({ titel, text, kinder }: { titel: string; text?: string; kinder?: ReactNode }) {
  return (
    <header className="fb-kopf">
      <div className="fb-kopf-innen">
        <span className="fb-marke"><Mail size={14} aria-hidden="true" /> Randori Mail · Abstimmung</span>
        <h1>{titel}</h1>
        {text && <p>{text}</p>}
        {kinder}
      </div>
    </header>
  );
}

function Fuss() {
  return (
    <footer className="fb-fuss">
      <p><Lock size={12} aria-hidden="true" /> Dieser Link ist persönlich — bitte nicht weitergeben.</p>
      <p>© {new Date().getFullYear()} Randori-Pro Management GmbH</p>
    </footer>
  );
}

function Uebersicht({ token }: { token: string }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const ansicht = params.get('ansicht') === 'kalender' ? 'kalender' : 'liste';
  const tokenOk = TOKEN_RE.test(token);
  // Nach An- oder Abmelden neu laden (Anmelde-Hinweis oben).
  const kennung = useAnmeldeKennung();

  const abfrage = useQuery({
    queryKey: ['link-uebersicht', token, kennung ?? 'niemand'],
    queryFn: () => linkUebersicht(token),
    enabled: tokenOk && kennung !== undefined,
    retry: 1,
    refetchInterval: 120_000,
  });
  const r = abfrage.data;
  const daten = r && r.ok ? r : null;
  // Ohne passende Anmeldung liefert die Datenbank keine Mails, nur den Anmeldestand.
  const fehlt = linkAnmeldungFehlt(r);
  const gesperrtName = r && !r.ok ? r.person?.name ?? null : null;
  const vorname = daten ? anredeName(daten.person.name) : gesperrtName ? anredeName(gesperrtName) : '';
  // Admin liest den Link einer anderen Person mit (Migration 0052): nur ansehen.
  const admin = daten?.admin_ansicht === true;

  useEffect(() => {
    document.title = vorname ? `${admin ? 'Admin-Ansicht: ' : ''}Abstimmung für ${vorname} · Randori Mail` : 'Abstimmung · Randori Mail';
  }, [vorname, admin]);

  // „Heute" vom Server — so stimmen die Wochen auch bei falsch gehender Uhr.
  const heuteIso = tagIso(alsTag(daten?.jetzt ?? null) ?? new Date());
  const heute = useMemo(() => alsTag(heuteIso)!, [heuteIso]);
  const mails = useMemo(() => daten?.mails ?? [], [daten]);
  const zahlen = useMemo(() => {
    const sonntag = plusTage(wochenStart(heute), 6);
    return {
      warten: mails.filter((m) => m.meine_anfrage?.antwort === 'offen' && m.status !== 'sent').length,
      woche: mails.filter((m) => {
        const t = alsTag(m.versand.am);
        return m.status !== 'sent' && !!t && t >= heute && t <= sonntag;
      }).length,
    };
  }, [mails, heute]);

  const monatRoh = /^(\d{4})-(\d{2})$/.exec(params.get('monat') ?? '');
  const monat = monatRoh ? new Date(Number(monatRoh[1]), Number(monatRoh[2]) - 1, 1) : new Date(heute.getFullYear(), heute.getMonth(), 1);

  const setzen = (werte: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, w] of Object.entries(werte)) { if (w) p.set(k, w); else p.delete(k); }
    setParams(p, { replace: true });
  };
  const oeffnen = (m: PlanungsMail) => navigate(`/feedback/${token}/${m.id}`);

  if (tokenOk && fehlt) {
    return <AnmeldeSeite anmeldung={fehlt} name={vorname} fuss={false} />;
  }

  if (!tokenOk || (r && !r.ok)) {
    return (
      <>
        <Kopf titel="Link nicht gültig" />
        <main className="fb-haupt">
          <div className="fb-hinweis-karte" role="alert">
            <Link2Off size={32} aria-hidden="true" />
            <p>{UNGUELTIG}</p>
          </div>
        </main>
      </>
    );
  }

  if (!daten && abfrage.isError) {
    return (
      <>
        <Kopf titel="Gerade keine Verbindung" />
        <main className="fb-haupt">
          <div className="fb-hinweis-karte" role="alert">
            <WifiOff size={32} aria-hidden="true" />
            <p>Die Mails ließen sich gerade nicht laden. Prüf Deine Internetverbindung und versuch es gleich noch einmal.</p>
            <button type="button" className="btn btn-primary" onClick={() => void abfrage.refetch()} disabled={abfrage.isFetching}>
              <RefreshCw size={16} aria-hidden="true" className={abfrage.isFetching ? 'spin' : undefined} /> Nochmal versuchen
            </button>
          </div>
        </main>
      </>
    );
  }

  if (!daten) {
    return (
      <>
        <Kopf titel="Einen Moment …" />
        <main className="fb-haupt">
          <div className="fb-laden" aria-live="polite"><div className="loading-spinner" /><span>Deine Mails werden geladen …</span></div>
        </main>
      </>
    );
  }

  return (
    <>
      <Kopf
        titel={`Hallo ${vorname}`}
        text={darfUeberLinkSchreiben(daten.anmeldung)
          ? `Hier siehst Du ${sichtSatz(daten.person)}. Öffne eine Mail, um sie zu kommentieren, Stellen zu markieren oder Formulierungen direkt zu ändern — oder gib einfach „Passt so“.`
          : `Hier siehst Du ${sichtSatz(daten.person)}. Öffne eine Mail, um sie anzusehen.`}
        kinder={(
          <ul className="fb-kpis" aria-label="Überblick">
            <li className={`fb-kpi${zahlen.warten ? ' fb-kpi--hervor' : ''}`}>
              <Hourglass size={15} aria-hidden="true" /><strong>{zahlen.warten}</strong> {zahlen.warten === 1 ? 'wartet' : 'warten'} auf Deine Rückmeldung
            </li>
            <li className="fb-kpi"><CalendarRange size={15} aria-hidden="true" /><strong>{zahlen.woche}</strong> {zahlen.woche === 1 ? 'geht' : 'gehen'} diese Woche raus</li>
          </ul>
        )}
      />

      <main className="fb-haupt">
        {admin
          ? <AdminAnsichtBanner anmeldung={daten.anmeldung} name={daten.person.name} />
          : <AnmeldeBanner anmeldung={daten.anmeldung} />}

        {darfUeberLinkSchreiben(daten.anmeldung) && (
          <section className="fb-schritte" aria-label="So gibst Du Rückmeldung">
            <div className="fb-schritt">
              <span className="fb-schritt-symbol" aria-hidden="true"><MessageSquareText size={18} /></span>
              <span><strong>Kommentieren</strong><span>Text markieren oder mit dem Zeichenstift einkreisen und dazu schreiben.</span></span>
            </div>
            <div className="fb-schritt">
              <span className="fb-schritt-symbol" aria-hidden="true"><PenLine size={18} /></span>
              <span><strong>Ändern vorschlagen</strong><span>Direkt in der Mail umschreiben. Die Mail ändert sich erst, wenn das Team zustimmt.</span></span>
            </div>
            <div className="fb-schritt">
              <span className="fb-schritt-symbol" aria-hidden="true"><CheckCircle2 size={18} /></span>
              <span><strong>Passt so</strong><span>Alles in Ordnung? Ein Klick genügt — dann weiß das Team Bescheid.</span></span>
            </div>
          </section>
        )}

        <div className="fb-werkzeug">
          <div className="seg-control" role="group" aria-label="Darstellung">
            <button type="button" className={`seg-btn${ansicht === 'liste' ? ' active' : ''}`} aria-pressed={ansicht === 'liste'} onClick={() => setzen({ ansicht: null })}>
              <List size={15} aria-hidden="true" /> Liste
            </button>
            <button type="button" className={`seg-btn${ansicht === 'kalender' ? ' active' : ''}`} aria-pressed={ansicht === 'kalender'} onClick={() => setzen({ ansicht: 'kalender' })}>
              <CalendarDays size={15} aria-hidden="true" /> Kalender
            </button>
          </div>
          <span className="fb-anzahl">{mails.length} {mails.length === 1 ? 'Mail' : 'Mails'}</span>
        </div>

        {ansicht === 'liste' ? (
          <PlanungsListe
            mails={mails}
            heute={heute}
            modus="alle"
            extern
            onOeffnen={oeffnen}
            leerText="Gerade liegt keine Mail zur Abstimmung bei Dir. Sobald wir Dich um Feedback bitten, steht sie hier."
            aktionen={(m) => (
              <>
                {admin && (
                  <Link to={`/geplante-mails/${m.id}`} className="btn btn-sm btn-ghost" title="Die Mail in der Werkstatt des Mailtools öffnen — dort kommentierst Du">
                    <ExternalLink size={14} aria-hidden="true" /> In der Werkstatt öffnen
                  </Link>
                )}
                <button type="button" className={`btn btn-sm ${!admin && m.meine_anfrage?.antwort === 'offen' && m.status !== 'sent' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => oeffnen(m)}>
                  {m.status === 'sent' || admin ? 'Ansehen' : 'Öffnen'} <ChevronRight size={14} aria-hidden="true" />
                </button>
              </>
            )}
          />
        ) : (
          <PlanungsKalender
            mails={mails}
            monat={monat}
            heute={heute}
            onMonat={(d) => setzen({ monat: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` })}
            onOeffnen={oeffnen}
          />
        )}
      </main>
    </>
  );
}

/**
 * Öffentliche Seite für Ansprechpersonen: /feedback/<token> (Überblick) und
 * /feedback/<token>/<mailId> (eine Mail). Ansehen, kommentieren und ändern
 * nur, wenn man mit dem Google-Konto der Adresse (@randori-pro.de) angemeldet
 * ist, für die der Link gilt — sonst zeigt die Seite nur die Anmeldung.
 * Admins sehen jeden Link mit (Admin-Ansicht, nur lesen). Die Datenbank
 * prüft jede Abfrage.
 */
export function FeedbackSeite() {
  const { token = '', mailId } = useParams();
  const navigate = useNavigate();
  useLinkSchutz();

  if (mailId) {
    return <FeedbackMailAnsicht token={token} mailId={mailId} onZurueck={() => navigate(`/feedback/${token}`)} />;
  }
  return (
    <div className="fb-seite">
      <Uebersicht token={token} />
      <Fuss />
    </div>
  );
}
